<template>
  <div>
    <SideBar :sidebar-open="sidebarOpen" @update:sidebar-open="(value) => (sidebarOpen = value)" />

    <div class="xl:pl-72">
      <div
        class="sticky top-0 z-40 flex h-16 shrink-0 items-center gap-x-6 border-b border-white/5 bg-background px-4 shadow-sm sm:px-6 lg:px-8"
      >
        <button type="button" id="sidebar" class="-m-2.5 p-2.5 text-white xl:hidden" @click="sidebarOpen = true">
          <span class="sr-only">{{ t("msg.openSidebar") }}</span>
          <Bars3Icon class="h-5 w-5" aria-hidden="true" />
        </button>

        <SearchBar :model-value="search" @update:model-value="(text) => (search = text)" />
      </div>

      <AdminHeader />

      <main>
        <div class="mx-auto px-12 py-10">
          <div class="flex items-start justify-between">
            <div class="flex flex-col space-y-3">
              <div class="flex items-center gap-3">
                <h1
                  tabindex="-1"
                  class="no-focus-outline scroll-m-20 text-3xl font-bold tracking-tight"
                  ref="initialFocus"
                >
                  {{ t("pages.agents.heading.title") }}
                </h1>
                <a
                  class="ml-2 inline-flex items-center gap-1 rounded-md border border-transparent bg-secondary px-2.5 py-0.5 text-xs font-semibold text-secondary-foreground transition-colors hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                  href="https://docs.knocknoc.io/books/admin-guide/page/agent-installation"
                  target="_blank"
                  :aria-label="`${t('pages.agents.docs')} (${t('commons.opensInNewTab')})`"
                >
                  {{ t("common.docs") }}
                  <ArrowTopRightOnSquareIcon class="h-3 w-3" />
                  <span class="sr-only">{{ t("common.opensInNewTab") }}</span>
                </a>
              </div>

              <div class="max-w-2xl">
                <p class="text-base leading-relaxed text-muted-foreground">
                  {{ t("pages.agents.heading.description") }}
                </p>
              </div>
            </div>

            <div class="flex flex-shrink-0 flex-col items-end">
              <button
                type="button"
                @click="
                  closeModal();
                  openModal();
                "
                class="inline-flex h-10 items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground shadow-sm ring-offset-background transition-all duration-200 hover:bg-primary/90 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0"
              >
                {{ t("pages.agents.heading.actions.create") }}
              </button>

              <Tooltip
                :aria-label="t('pages.agents.tooltip.currentAgentVersion')"
                :focusable="agentUpdateRequired"
                :text="t('pages.agents.tooltip.currentAgentVersion')"
                side="bottom"
              >
                <span
                  v-if="agentUpdateRequired"
                  class="mt-3 inline-flex items-center gap-1 rounded-md bg-green-100 px-1 font-medium text-green-800 ring-1 ring-green-200 hover:bg-green-200 focus-visible:outline-green-600 dark:bg-green-900/20 dark:text-green-400 dark:ring-green-800/30 dark:hover:bg-green-900/30"
                >
                  <CheckCircleIcon class="h-3.5 w-3.5" />
                  {{ newestAgentVersion.version }}
                </span>
              </Tooltip>
            </div>
          </div>

          <div class="mt-8 flow-root min-h-screen overflow-x-auto">
            <AutoTable :columns="agentTableCols" :rows="filterData">
              <template #default>
                <TableRow>
                  <TableCell :colspan="agentTableCols?.length" class="text-center text-muted-foreground">
                    <div class="flex items-center justify-center gap-2">
                      <InformationCircleIcon class="h-4 w-4" aria-hidden="true" />
                      <span>
                        {{ t("common.getStarted", { button: t("pages.agents.heading.actions.create") }) }}
                      </span>
                    </div>
                  </TableCell>
                </TableRow>
              </template>

              <template #col-Capabilities="{ row: agent }">
                <template v-if="agent.LastSeen">
                  <button
                    class="inline-flex items-center gap-1 text-left"
                    :class="{ 'opacity-60': isAgentStale(agent) }"
                    @click="
                      closeModal();
                      currentAgent = agent;
                      capabilitiesModal = true;
                    "
                  >
                    <span>
                      <template v-if="agent.Capabilities['os-release']">
                        {{ agent.Capabilities["os-release"].NAME }}
                        {{ agent.Capabilities["os-release"].VERSION }}
                      </template>
                      <template v-else>{{ t("msg.unknown") }}</template>
                    </span>
                    <ShieldCheckIcon class="h-4 w-4 flex-shrink-0" />
                  </button>
                </template>
                <template v-else>
                  <p>{{ t("msg.awaitingConnection") }}</p>
                  <CopySpan aria-label="Copy agent token" show-icon="left" :value="agent.Token" />
                </template>
              </template>

              <template #col-LastSeen="{ row: agent }">
                <template v-if="agent.LastSeen">
                  <Tooltip
                    aria-label="Agent appears stale or dead"
                    :focusable="true"
                    :text="isAgentStale(agent) ? 'This agent appears to be stale or dead' : undefined"
                  >
                    <span
                      class="inline-flex items-center gap-1.5"
                      :class="{
                        'inline-flex items-center gap-1 rounded-md bg-yellow-100 px-2 py-1 font-medium text-yellow-800 ring-1 ring-yellow-200 hover:bg-yellow-200 focus-visible:outline-yellow-600 dark:bg-yellow-900/20 dark:text-yellow-400 dark:ring-yellow-800/30 dark:hover:bg-yellow-900/30':
                          isAgentStale(agent),
                      }"
                    >
                      <ExclamationTriangleIcon
                        v-if="isAgentStale(agent)"
                        class="h-3.5 w-3.5 flex-shrink-0"
                        aria-hidden="true"
                      />
                      {{ t("fields.agents.lastSeen", { time: DateTime.fromISO(agent.LastSeen).toRelative() }) }}
                    </span>
                  </Tooltip>
                </template>
                <template v-else>{{ t("msg.awaitingConnection") }}</template>
              </template>

              <template #col-Version="{ row: agent }">
                <template v-if="agent.LastSeen">
                  <template v-if="agent.Version">
                    <template v-if="agentUpdateRequired">
                      <template v-if="isAgentIncompatible(agent)">
                        <Tooltip
                          aria-label="Agent version incompatible"
                          :focusable="true"
                          :text="`${t('pages.header.thisIncompatibleAgentVersion')}. ${t('fields.agents.versionRequired', { version: minRequiredAgentVersion.version })}`"
                        >
                          <span
                            class="inline-flex items-center gap-1 rounded-md bg-red-100 px-2 py-1 font-medium text-red-800 ring-1 ring-red-200 hover:bg-red-200 focus-visible:outline-red-600 dark:bg-red-900/20 dark:text-red-400 dark:ring-red-800/30 dark:hover:bg-red-900/30"
                          >
                            <ExclamationTriangleIcon class="h-3.5 w-3.5" />
                            {{ agent.Version }}
                          </span>
                        </Tooltip>
                      </template>
                      <template v-else-if="isAgentOutdated(agent)">
                        <Tooltip
                          :aria-label="`This agent is out of date, please upgrade it to version ${newestAgentVersion.version}`"
                          :focusable="true"
                          text="This Agent is out of date.&#10;&#13;Apply operating system updates to upgrade the Agent to the latest version."
                        >
                          <span
                            class="inline-flex items-center gap-1 rounded-md bg-yellow-100 px-2 py-1 font-medium text-yellow-800 ring-1 ring-yellow-200 hover:bg-yellow-200 focus-visible:outline-yellow-600 dark:bg-yellow-900/20 dark:text-yellow-400 dark:ring-yellow-800/30 dark:hover:bg-yellow-900/30"
                          >
                            <ExclamationTriangleIcon class="h-3.5 w-3.5" />
                            {{ agent.Version }}
                          </span>
                        </Tooltip>
                      </template>
                      <template v-else>
                        <Tooltip aria-label="Agent up to date" :focusable="true" text="This agent is up to date">
                          <span
                            class="inline-flex items-center gap-1 rounded-md bg-green-100 px-2 py-1 font-medium text-green-800 ring-1 ring-green-200 hover:bg-green-200 focus-visible:outline-green-600 dark:bg-green-900/20 dark:text-green-400 dark:ring-green-800/30 dark:hover:bg-green-900/30"
                          >
                            <CheckCircleIcon class="h-3.5 w-3.5" />
                            {{ agent.Version }}
                          </span>
                        </Tooltip>
                      </template>
                    </template>
                    <template v-else>
                      <span :class="{ 'opacity-60': isAgentStale(agent) }">
                        {{ agent.Version }}
                      </span>
                    </template>
                  </template>
                  <template v-else>{{ t("msg.unknown") }}</template>
                </template>
                <template v-else>{{ t("msg.awaitingConnection") }}</template>
              </template>

              <template #col-ExternalIP="{ row: agent }">
                <template v-if="agent.LastSeen">
                  <span :class="{ 'opacity-60': isAgentStale(agent) }">
                    <template v-if="agent.ExternalIP">{{ ipWithoutPort(agent.ExternalIP) }}</template>
                    <template v-else>{{ t("msg.unknown") }}</template>
                  </span>
                </template>
                <template v-else>{{ t("msg.awaitingConnection") }}</template>
              </template>

              <template #col-Edit="{ row: agent }">
                <Menu as="div" class="inline-block">
                  <div>
                    <MenuButton
                      class="inline-flex w-full justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white/75"
                    >
                      {{ t("msg.actions") }}
                      <ChevronDownIcon
                        class="-mr-1 ml-2 h-5 w-5 text-violet-200 hover:text-violet-100"
                        aria-hidden="true"
                      />
                    </MenuButton>
                  </div>

                  <transition
                    enter-active-class="transition duration-100 ease-out"
                    enter-from-class="transform scale-95 opacity-0"
                    enter-to-class="transform scale-100 opacity-100"
                    leave-active-class="transition duration-75 ease-in"
                    leave-from-class="transform scale-100 opacity-100"
                    leave-to-class="transform scale-95 opacity-0"
                  >
                    <MenuItems
                      anchor="bottom end"
                      class="absolute right-0 z-50 mt-2 w-56 origin-top-right divide-y divide-[#e05454] rounded-md bg-background shadow-lg ring-1 ring-[#e05454] focus:outline-none"
                    >
                      <div class="px-1 py-1">
                        <MenuItem>
                          <button
                            @click="
                              currentAgent = agent;
                              viewModal = true;
                            "
                            class="group flex w-full items-center rounded-md px-2 py-2 text-sm text-white"
                          >
                            <EyeIcon class="mr-2 h-5 w-5 text-[#e05454]" aria-hidden="true" />
                            {{ t("msg.view") }}
                          </button>
                        </MenuItem>
                      </div>
                      <div class="px-1 py-1">
                        <MenuItem>
                          <button
                            @click="
                              currentAgent = agent;
                              openModal();
                            "
                            class="group flex w-full items-center rounded-md px-2 py-2 text-sm text-white"
                          >
                            <PencilSquareIcon class="mr-2 h-5 w-5 text-[#e05454]" aria-hidden="true" />
                            {{ t("msg.edit") }}
                          </button>
                        </MenuItem>
                      </div>
                      <div class="px-1 py-1">
                        <MenuItem>
                          <button
                            @click="
                              currentAgent = agent;
                              deleteModal = true;
                            "
                            class="group flex w-full items-center rounded-md px-2 py-2 text-sm text-white"
                            aria-label="Delete agent"
                          >
                            <TrashIcon class="mr-2 h-5 w-5 text-[#e05454]" aria-hidden="true" />
                            {{ t("msg.delete") }}
                          </button>
                        </MenuItem>
                      </div>
                    </MenuItems>
                  </transition>
                </Menu>
              </template>
            </AutoTable>
          </div>
        </div>
      </main>
    </div>
  </div>

  <Dialog :show="deleteModal" v-if="currentAgent">
    <DialogOverlay />

    <DialogPanel>
      <DialogTitle>
        {{ t("msg.deleteAgent") }}
      </DialogTitle>

      <DialogDescription>
        {{ t("msg.confirmDelete", { name: currentAgent.Name }) }}
      </DialogDescription>

      <div class="flex space-x-3">
        <button
          type="button"
          class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
          @click="closeModal"
          ref="cancelButtonRef"
        >
          {{ t("msg.cancel") }}
        </button>

        <button
          type="button"
          class="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#e05454] sm:col-start-2"
          @click="deleteAgent()"
        >
          {{ t("msg.delete") }}
        </button>
      </div>
    </DialogPanel>
  </Dialog>

  <Dialog :show="createModal">
    <DialogOverlay />

    <DialogPanel>
      <DialogTitle>
        {{ currentAgent == null ? t("pages.agents.heading.actions.create") : t("pages.agents.heading.actions.update") }}
      </DialogTitle>

      <div>
        <Input
          type="text"
          model-label="fields.agents.name"
          model-name="name"
          :model-value="agentName"
          placeholder="Agent"
          @update:model-value="(input) => (agentName = input)"
          color="rose"
        />
      </div>

      <div class="text-right underline">
        <a
          href="https://docs.knocknoc.io/books/admin-guide/page/agent-installation"
          target="_blank"
          :aria-label="`${t('pages.agents.docs')} (${t('common.opensInNewTab')})`"
        >
          view the docs
        </a>
      </div>

      <div class="flex space-x-3">
        <button
          type="button"
          class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
          @click="closeModal"
          ref="cancelButtonRef"
        >
          {{ t("msg.cancel") }}
        </button>

        <button
          type="button"
          class="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-white shadow-sm focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#e05454] sm:col-start-2"
          @click="createAgent()"
        >
          {{ currentAgent == null ? t("msg.create") : t("msg.update") }}
        </button>
      </div>
    </DialogPanel>
  </Dialog>

  <Dialog :show="capabilitiesModal" v-if="currentAgent">
    <DialogOverlay />

    <DialogPanel>
      <AgentCapabilitesComponent :current-agent="currentAgent" />

      <button
        type="button"
        class="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
        @click="closeModal"
        ref="cancelButtonRef"
      >
        {{ t("common.close") }}
      </button>
    </DialogPanel>
  </Dialog>

  <Dialog :show="viewFirstModal || viewModal" v-if="currentAgent">
    <DialogOverlay />

    <DialogPanel>
      <DialogTitle>
        <template v-if="viewFirstModal">
          {{ t("pages.agents.dialogs.createdAgent.header") }}
        </template>
        <template v-else-if="viewModal">
          {{ t("msg.agentToken") }}
        </template>
      </DialogTitle>

      <DialogDescription class="flex flex-col">
        <div class="mb-2">
          <h2 class="font-bold">{{ t("pages.agents.dialogs.createdAgent.linux.title") }}</h2>
          <i18n-t keypath="pages.agents.dialogs.createdAgent.linux.description">
            <template #link>
              <a
                href="https://docs.knocknoc.io/books/admin-guide/page/agent-installation"
                target="_blank"
                :aria-label="`${t('pages.agents.docs')} (${t('common.opensInNewTab')})`"
                class="underline"
                >Agent install docs.</a
              >
            </template>
          </i18n-t>
        </div>
        <CodeBlock class="my-2" code="curl -sSL 'https://packages.knocknoc.io/setup/setup_knocknoc_agent.sh' | bash" />
        <hr class="mb-2" />
        <div class="mb-2">
          <h2 class="font-bold">{{ t("pages.agents.dialogs.createdAgent.windows.title") }}</h2>
          <i18n-t keypath="pages.agents.dialogs.createdAgent.windows.description">
            <template #link>
              <a
                :aria-label="`${t('pages.agents.docs')} (${t('common.opensInNewTab')})`"
                class="underline"
                href="https://docs.knocknoc.io/books/admin-guide/page/windows-agent-installation"
                target="_blank"
                >Windows agent install docs.</a
              >
            </template>
          </i18n-t>
        </div>

        <hr class="mb-2" />
        <div class="space-y-2">
          <div>{{ t("pages.agents.dialogs.createdAgent.agentHostnameCopy") }}</div>
          <CodeBlock
            :aria-label="t('pages.agents.dialogs.createdAgent.copyHostname')"
            :code="getCurrentHostname()"
            class="text-primary"
            data-testid="current_hostname"
            show-icon="none"
          />

          <div>{{ t("pages.agents.dialogs.createdAgent.agentTokenCopy") }}</div>
          <CodeBlock
            class="text-primary"
            :aria-label="t('pages.agents.dialogs.createdAgent.copyToken')"
            :code="currentAgent.Token"
            show-icon="none"
            data-testid="modal_agent_token"
          />

          <span v-if="viewFirstModal">{{ t("pages.agents.dialogs.createdAgent.agentTokenView") }}</span>
        </div>
      </DialogDescription>

      <button
        type="button"
        class="inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:col-start-1 sm:mt-0"
        @click="closeModal"
        ref="cancelButtonRef"
      >
        {{ t("common.close") }}
      </button>
    </DialogPanel>
  </Dialog>

  <ErrorNotification :error="errorMessage" :title="t('msg.error')" @update:error="(input) => (errorMessage = input)" />

  <InfoNotification :text="infoMessage" @update:info="(input) => (infoMessage = input)" />
</template>

<script lang="ts" setup>
import { DialogDescription, Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/vue";
import {
  Bars3Icon,
  ChevronDownIcon,
  EyeIcon,
  PencilSquareIcon,
  ShieldCheckIcon,
  TrashIcon,
} from "@heroicons/vue/20/solid";
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from "vue";
import { DateTime } from "luxon";

import Input from "../../components/Input.vue";
import SideBar from "../../components/SideBar.vue";
import SearchBar from "../../components/SearchBar.vue";
import ErrorNotification from "../../components/ErrorNotification.vue";
import InfoNotification from "../../components/InfoNotification.vue";
import userService from "../../services/user.service.ts";
import Dialog from "../../components/ui/dialog/Dialog.vue";
import DialogOverlay from "../../components/ui/dialog/DialogOverlay.vue";
import DialogPanel from "../../components/ui/dialog/DialogPanel.vue";
import DialogTitle from "../../components/ui/dialog/DialogTitle.vue";
import TableRow from "../../components/ui/table/TableRow.vue";
import TableCell from "../../components/ui/table/TableCell.vue";
import CodeBlock from "../../components/CodeBlock.vue";

import globals from "../../globals";
import { useI18n } from "vue-i18n";
import AdminHeader from "../../components/AdminHeader.vue";
import {
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
} from "@heroicons/vue/24/outline";
import Tooltip from "../../components/ui/Tooltip.vue";
import { useAdminStore } from "../../pinia/admin.ts";
import { useUserStore } from "../../pinia/user.ts";
import CopySpan from "../../components/CopySpan.vue";
import AutoTable from "../../components/ui/table/AutoTable.vue";
import AgentCapabilitesComponent from "./Agents/AgentCapabilitesComponent.vue";

import { parseKnocknocVersion } from "../../utils/versions";

const { t } = useI18n();

let dataInterval;

const adminStore = useAdminStore();
adminStore.fetchStatus();

const userStore = useUserStore();

const initialFocus = ref(null);

const errorMessage = ref("");
const infoMessage = ref("");
const search = ref("");
const createModal = ref(false);
const deleteModal = ref(false);
const viewModal = ref(false);
const viewFirstModal = ref(false);
const capabilitiesModal = ref(false);
const sidebarOpen = ref(false);

const availableAgentVersions = ref(adminStore.user?.AvailableAgentVersions);
const minRequiredAgentVersion = ref(adminStore.user?.MinimumRequiredAgentVersion);
const newestAgentVersion = computed(() =>
  availableAgentVersions.value?.reduce((acc, x) => (parseKnocknocVersion(x.version).gtes(acc.version) ? x : acc)),
);
const agentUpdateRequired = computed(() =>
  agents.value?.some((item) => {
    if (newestAgentVersion.value) {
      return item.Version && parseKnocknocVersion(newestAgentVersion.value.version).gtes(item.Version);
    }
  }),
);

const currentAgent = ref(null);

const agentName = ref("");

const agents = ref([]);

function isAgentOutdated(agent) {
  if (newestAgentVersion.value) {
    return parseKnocknocVersion(newestAgentVersion.value.version).gts(agent.Version);
  }
  return false;
}

function isAgentStale(agent) {
  if (!agent?.LastSeen) {
    return false;
  }
  const timeRemainingMillis = globals.timeRemaining(agent.LastSeen);
  const twoHoursMillis = 2 * 60 * 60 * 1000;
  return timeRemainingMillis < -1 * twoHoursMillis;
}

function isAgentIncompatible(agent) {
  if (minRequiredAgentVersion.value) {
    return parseKnocknocVersion(minRequiredAgentVersion.value.version).gts(agent.Version);
  }
  return false;
}

const agentTableCols = [
  { key: "Name", label: t("fields.agents.name"), sortable: true },
  { key: "Capabilities", label: t("fields.agents.capabilities"), sortable: false },
  { key: "LastSeen", label: t("fields.agents.activity"), sortable: true },
  { key: "Version", label: t("fields.agents.version"), sortable: true },
  { key: "ExternalIP", label: t("fields.agents.ipAddress"), sortable: true },
  { key: "Edit", hidden: true },
];

const filterData = computed(() => {
  if (!agents.value) return [];
  const searchTerm = search.value?.toString().toLocaleLowerCase() ?? "";

  return agents.value.filter((item) => {
    if (!searchTerm) return true;
    return Object.values(item).some((val) => val?.toString().toLocaleLowerCase().includes(searchTerm) ?? false);
  });
});

const closeModal = () => {
  createModal.value = false;
  deleteModal.value = false;
  viewModal.value = false;
  viewFirstModal.value = false;
  capabilitiesModal.value = false;
  currentAgent.value = null;
  agentName.value = "";
};

const openModal = () => {
  if (currentAgent.value !== null) {
    agentName.value = currentAgent.value.Name;
  }
  createModal.value = true;
};

const deleteAgent = () => {
  if (currentAgent.value !== null) {
    userService.deleteAgent(currentAgent.value.ID).then(
      (response) => {
        fetchData();
        infoMessage.value = t("msg.agentDeleted");
      },
      (error) => {
        fetchData();
        errorMessage.value = globals.renderError(t, error);
      },
    );
  }
  deleteModal.value = false;
  currentAgent.value = null;
};

const createAgent = () => {
  const data = {
    ID: currentAgent.value?.ID,
    Name: agentName.value,
    Token: currentAgent.value?.Token,
  };

  if (currentAgent.value !== null) {
    userService.updateAgent(currentAgent.value.ID, data).then(
      (response) => {
        fetchData();
        infoMessage.value = t("msg.agentUpdated");
      },
      (error) => {
        fetchData();
        errorMessage.value = globals.renderError(t, error);
      },
    );
  } else {
    userService.createAgent(JSON.stringify(data)).then(
      (response) => {
        fetchData(data.Name);
        infoMessage.value = t("msg.agentCreated");
      },
      (error) => {
        fetchData();
        errorMessage.value = globals.renderError(t, error);
      },
    );
  }
  closeModal();
};

const fetchData = (name = null) => {
  userStore.fetchAgents({ force: true }).then(
    (response) => {
      agents.value = response.data;
      if (name !== null) {
        const agent = agents.value.find((agent) => agent.Name === name);
        if (agent) {
          currentAgent.value = agent;
          viewFirstModal.value = true;
        }
      }
    },
    (error) => {
      errorMessage.value = globals.renderError(t, error);
    },
  );
};

const ipWithoutPort = (address) => {
  const colonIndex = address.lastIndexOf(":");
  if (colonIndex !== -1 && address.indexOf(":") === colonIndex) {
    return address.substring(0, colonIndex);
  }
  return address;
};

watch(initialFocus, async (el) => {
  if (el) {
    await nextTick();
    el.focus();
  }
});

onMounted(() => {
  fetchData();
  dataInterval = setInterval(fetchData, 5000);
});

onUnmounted(() => {
  clearInterval(dataInterval);
});

function getCurrentHostname(): string {
  try {
    return window.location.hostname;
  } catch (e) {
    console.error("Error getting hostname:", e);
    return "Unknown host";
  }
}
</script>
